import { BidContext } from '@/pages/Home/HomePage';
import useToken from '@/utils/useToken';
import React, { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Button,
  Icon,
  LoadingSpinner,
} from '@data-products-and-ai/react-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);
const color = '#8a00e5';

const PredictionGraph: React.FC = () => {
  const selectionBid = React.useContext(BidContext);
  const getToken = useToken();
  const chartRef = useRef<ChartJS<'bar', number[][], string> | null>(null);

  interface PredictionData {
    labels: string[];
    datasets: {
      label: string;
      data: number[][];
      backgroundColor: string[];
    }[];
  }
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Prediction Graph',
      },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function (context: any) {
            return predictionDataTooltip[context.parsed.x];
          },
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'xy' as 'x' | 'y' | 'xy',
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'xy' as 'x' | 'y' | 'xy',
        },
      },
    },
    scales: {
      x: {
        title: {
          display: false,
          text: '',
        },
        min: 0,
        max: 10,
      },
      y: {
        title: {
          display: true,
          text: 'Predicted',
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'linear' as const,
    },
  };

  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  const [predictionData, setPredictionData] = React.useState<PredictionData>({
    labels: [],
    datasets: [],
  });

  const [predictionDataTooltip, setpredictionDataTooltip] = React.useState<
    string[]
  >([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getToken();
      fetch(`${import.meta.env.VITE_APP_API_URL}/reasoning_route`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.accessToken}`,
        },
        body: JSON.stringify(selectionBid),
      })
        .then((response) => response.json())
        .then((data) => {
          const plotData = data.SHAP_Contribution_Plot;
          const parsedData = parseData(plotData.data[1], plotData.layout);
          setPredictionData(parsedData);
          setpredictionDataTooltip(plotData.data[1].text ?? []);
        })
        .catch((error) => {
          console.log('Error fetching data from the API', error);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    if (!Object.keys(selectionBid).length) return;
    fetchData();
  }, [selectionBid]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseData = (plotData: any, layout: any) => {
    const data = plotData.y
    const labels = plotData.x;
    const backgroundColor = plotData.marker.color;
    const label = layout.title.text;

    const finalData = [];
    let total = 0;
    for (let i = 0; i < data.length; i++) {
      const vStart = total;
      total += data[i];
      finalData.push([vStart, total]);
    }

    const returnData = {
      labels: labels,
      datasets: [
        {
          label: label,
          data: finalData,
          backgroundColor: backgroundColor,
        },
      ],
    };
    return returnData;
  };

  return (
    <div>
      <div className="predictionGraph_top-bar">
        <h5>Prediction</h5>
        <Button
          type="primaryOutline"
          allCaps={false}
          size="small"
          onClick={handleResetZoom}
          icon={<Icon icon="IconZoomIn" iconSize={0.9} color={color} />}
        >
          Reset
        </Button>
      </div>
      {loading ? (
        <LoadingSpinner
          color="#8a00e5"
          backgroundColor="#ADA9C2"
          text="Loading..."
        />
      ) : (
        <Bar
          ref={chartRef}
          data={predictionData}
          options={options}
          height={'200px'}
        />
      )}
    </div>
  );
};

export default PredictionGraph;
